import { formatOddsAs } from '@hienbet/shared';
import type {
  LiveBookState,
  LiveMessage,
  MarketView,
  NameStyle,
  OddsFormat,
} from '@hienbet/shared';

/**
 * Server access.
 *
 * In dev, Vite proxies /api and /live to localhost:8080. In production the SPA
 * is on Cloudflare Pages and the server is on the droplet, so VITE_API_BASE
 * points at it and every request carries credentials for the session cookie.
 */
const API_BASE = (import.meta.env['VITE_API_BASE'] as string | undefined) ?? '';

export function liveSocketUrl(): string {
  if (API_BASE) {
    return API_BASE.replace(/^http/, 'ws') + '/live';
  }
  const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${protocol}://${location.host}/live`;
}

export function loginUrl(): string {
  return `${API_BASE}/auth/login?return_to=${encodeURIComponent(location.origin + '/')}`;
}

const TOKEN_KEY = 'hienbet.session';

/**
 * Session token.
 *
 * The site and the API are on different domains, so a cookie cannot be relied
 * on — Safari and every iOS browser refuse third-party cookies, which would
 * lock out a large share of viewers entirely. The server hands the session over
 * in the URL fragment after login and the SPA keeps it here.
 */
export function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null; // private mode with storage disabled
  }
}

export function storeToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* nothing we can do; the session simply will not persist */
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Pick up the token the server put in the fragment, then scrub it from the
 * address bar so it is not left in history or copy-pasted by a viewer.
 */
export function captureTokenFromUrl(): boolean {
  const hash = location.hash;
  if (!hash.includes('token=')) return false;

  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const token = params.get('token');
  if (!token) return false;

  storeToken(token);
  history.replaceState(null, '', location.pathname + location.search);
  return true;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = readToken();
  const headers: Record<string, string> = {};
  if (init.body) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    // Still sent for same-origin deployments, where the cookie is preferable.
    credentials: 'include',
    ...init,
    headers: { ...headers, ...(init.headers as Record<string, string> | undefined) },
  });

  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : {};

  if (!res.ok) {
    const problem = body as { error?: string; message?: string };
    throw new ApiError(problem.message ?? `Request failed (${res.status})`, problem.error ?? 'error');
  }
  return body as T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
  }
}

export interface Me {
  loggedIn: boolean;
  /** True only for Hien — unlocks the fulfilment queue page. */
  isBroadcaster?: boolean;
  user?: {
    id: string;
    login: string;
    displayName: string;
    avatarUrl: string;
    isSubscriber: boolean;
  };
  balance?: number;
  dailyClaim?: { available: boolean; amount: number };
}

export interface BetRecord {
  id: string;
  marketId: string;
  selection: string;
  /** "Her kills 8.5" — the market and line this bet lives in. */
  market: string;
  /** In-play micro-market bet (next kill, dies-before) — rendered compactly. */
  micro: boolean;
  stake: number;
  milliOdds: number;
  status: 'open' | 'won' | 'lost' | 'void';
  payout: number;
  placedAt: number;
}

export interface LeaderRow {
  rank: number;
  displayName: string;
  avatarUrl: string;
  balance: number;
  style?: NameStyle;
}

export const api = {
  me: () => request<Me>('/api/me'),
  accounts: <T>() => request<{ accounts: T[] }>('/api/accounts'),
  // Typed loosely here; the Ledger page owns the detailed shape.
  accountsDetail: <T = any>() => request<T>('/api/accounts/detail'),
  logout: async () => {
    const result = await request<{ ok: boolean }>('/auth/logout', { method: 'POST' });
    clearToken();
    return result;
  },
  claimDaily: () => request<{ claimed: boolean; amount: number; balance: number }>('/api/daily-claim', { method: 'POST' }),
  book: () => request<{ live: boolean; phase: string; markets: MarketView[]; balance: number | null }>('/api/book'),
  bets: () =>
    request<{ balance: number; goldTickets?: boolean; parlays?: ParlayRecord[]; bets: BetRecord[] }>(
      '/api/bets',
    ),
  ledger: () => request<{ balance: number; entries: Array<{ id: number; delta: number; kind: string; memo: string; at: number }> }>('/api/ledger'),
  leaderboard: () => request<{ players: LeaderRow[] }>('/api/leaderboard'),
  placeBet: (input: { marketId: string; selectionId: string; stake: number; expectedMilliOdds: number }) =>
    request<{ ok: boolean; betId: string; milliOdds: number; balance: number }>('/api/bets', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  placeParlay: (input: {
    legs: Array<{ marketId: string; selectionId: string; expectedMilliOdds: number }>;
    stake: number;
  }) =>
    request<{ ok: boolean; parlayId: string; combinedMilli: number; balance: number }>('/api/parlays', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  results: () =>
    request<{
      games: Array<{
        id: string;
        at: number;
        account: string | null;
        voided: boolean;
        champion: string | null;
        kills: number | null;
        deaths: number | null;
        assists: number | null;
        result: string | null;
        durationSec: number | null;
        bets: number;
        bettors: number;
        staked: number;
        topWinner: { name: string; profit: number } | null;
        wagers: Array<{
          who: string;
          stake: number;
          status: string;
          profit: number;
          marketLabel: string;
        }>;
      }>;
    }>('/api/results'),
};

export interface ParlayRecord {
  id: string;
  stake: number;
  combinedMilli: number;
  status: string;
  payout: number;
  placedAt: number;
  legs: Array<{ marketLabel: string; selectionLabel: string; milliOdds: number; result: string }>;
}

export type LiveHandler = (message: LiveMessage) => void;

/**
 * Live feed with reconnect.
 *
 * Everything arriving here is delay-buffered server-side, so it is safe to
 * render the instant it lands — it is already behind the broadcast.
 */
export function connectLive(onMessage: LiveHandler, onStatus?: (up: boolean) => void): () => void {
  let socket: WebSocket | null = null;
  let closed = false;
  let retry = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const open = () => {
    if (closed) return;
    // A browser cannot set headers on a WebSocket, so the session rides in the
    // subprotocol. That keeps it out of the URL, and therefore out of server
    // logs and browser history. Without a token the socket still connects — the
    // board is public; only personal balance updates need identifying.
    const token = readToken();
    socket = token
      ? new WebSocket(liveSocketUrl(), [`sb.${token}`])
      : new WebSocket(liveSocketUrl());

    socket.onopen = () => {
      retry = 0;
      onStatus?.(true);
    };

    socket.onmessage = (event) => {
      try {
        onMessage(JSON.parse(event.data as string) as LiveMessage);
      } catch {
        /* ignore malformed frame */
      }
    };

    socket.onclose = () => {
      onStatus?.(false);
      socket = null;
      if (closed) return;
      const delay = Math.min(15_000, 800 * 2 ** retry++);
      timer = setTimeout(open, delay);
    };

    socket.onerror = () => socket?.close();
  };

  open();

  return () => {
    closed = true;
    if (timer) clearTimeout(timer);
    socket?.close();
  };
}

export const emptyBook: LiveBookState = {
  live: false,
  phase: 'idle',
  gameTime: null,
  lockInSeconds: null,
  markets: [],
};

export function formatChips(n: number): string {
  return n.toLocaleString('en-US');
}

const ODDS_FORMAT_KEY = 'hienbet.oddsFormat';

/** American by default; the preference sticks per browser. */
export function readOddsFormat(): OddsFormat {
  return localStorage.getItem(ODDS_FORMAT_KEY) === 'decimal' ? 'decimal' : 'american';
}

export function writeOddsFormat(format: OddsFormat): void {
  localStorage.setItem(ODDS_FORMAT_KEY, format);
}

export function formatOddsValue(milliOdds: number, format: OddsFormat = readOddsFormat()): string {
  return formatOddsAs(milliOdds, format);
}

export function formatClock(seconds: number | null): string {
  if (seconds === null) return '--:--';
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
